import React, { useMemo, useState } from 'react';
import { Shuffle, TriangleAlert } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  buildRepostCaption, checkRepostCaptionTemplate, templateNotes, listSpins, normaliseTemplate, seededRandom,
} from '../lib/repostCaptionTemplate';

/*
 * Setting 723 "Repost Caption Template": a live preview under the box - what ONE reposted post would get, for a sample
 * source account and a sample original caption the customer can change (owner's example: 13_beard_86). The rules are
 * lib/repostCaptionTemplate.js - a PORT of the worker's caption building and of the server's save check, pinned by
 * fixtures each of them writes (verification items 1-5, 2026-10-13) - so the caption, its length and the notes shown here
 * are what the worker posts and what saving will say. "Spin again" draws the next spin (each post gets its own); a template with up to
 * 50 versions lists them all, so nested spintax can be checked by eye. Text only: the template, the samples and the
 * result are React text nodes, never HTML.
 */

export const SAMPLE_SOURCE = '13_beard_86';
export const SAMPLE_CAPTION = 'Daily beard inspo #beard #beardstyle';
const MAX_LISTED = 50;

const NOTE_CLASS = {
  error: 'text-destructive',
  warning: 'text-warning',
  info: 'text-muted-foreground',
};

export default function RepostCaptionPreview({ template }) {
  const [sampleSource, setSampleSource] = useState(SAMPLE_SOURCE);
  const [sampleCaption, setSampleCaption] = useState(SAMPLE_CAPTION);
  const [spinNo, setSpinNo] = useState(1);

  const check = useMemo(() => checkRepostCaptionTemplate(template), [template]);
  const notes = useMemo(() => templateNotes(check), [check]);
  // One seed per "Spin again": typing keeps the same draw, so the preview does not jump on every key.
  const built = useMemo(
    () => buildRepostCaption(template, { sourceUsername: sampleSource, originalCaption: sampleCaption, rnd: seededRandom(spinNo) }),
    [template, sampleSource, sampleCaption, spinNo],
  );
  const versions = useMemo(
    () => (!check.blank && check.versions > 1 && check.versions <= MAX_LISTED ? listSpins(normaliseTemplate(template), MAX_LISTED) : null),
    [check, template],
  );

  if (check.blank) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="repost-caption-preview-empty">
        Empty: reposted posts keep your caption settings (631/632, 652, 665). Write a template to see a preview here.
      </p>
    );
  }

  const facts = [`${built.length.toLocaleString('en-US')} character${built.length === 1 ? '' : 's'} of the 2,200 Instagram allows`];
  if (built.shortened) facts.push('the original caption was shortened to fit');
  if (built.droppedOriginal) facts.push('the original caption was left out: your own text is over the limit');
  if (check.groups > 0) {
    const n = check.versionsCapped ? 'over a million' : (versions ? versions.length : check.versions).toLocaleString('en-US');
    facts.push(`${n} possible version${n === '1' ? '' : 's'} of your text${check.depth > 1 ? ` (spintax nested ${check.depth} deep)` : ''}`);
  }

  return (
    <div className="space-y-3 rounded-xl border border-border bg-secondary/40 p-3" data-testid="repost-caption-preview">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">Preview: one reposted post</p>
        {check.groups > 0 && (
          <Button
            type="button" variant="outline" size="sm" className="tap rounded-full"
            onClick={() => setSpinNo((n) => n + 1)}
            data-testid="repost-caption-preview-spin"
          >
            <Shuffle className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Spin again
          </Button>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Source account (sample)</span>
          <Input
            value={sampleSource}
            onChange={(e) => setSampleSource(e.target.value)}
            className="bg-background text-sm"
            data-testid="repost-caption-preview-source"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Original caption (sample; empty it to see a post without one)</span>
          <Textarea
            rows={2}
            value={sampleCaption}
            onChange={(e) => setSampleCaption(e.target.value)}
            className="bg-background text-sm"
            data-testid="repost-caption-preview-original"
          />
        </label>
      </div>

      {built.missingSource ? (
        <p className="flex items-start gap-2 text-sm text-warning" data-testid="repost-caption-preview-output">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>No source account: this spin uses &lt;sourceusername&gt;, so a post without one is not queued (it stays for a later scrape).</span>
        </p>
      ) : (
        <div
          className="whitespace-pre-wrap break-words rounded-md border border-border bg-background p-3 text-sm text-foreground"
          data-testid="repost-caption-preview-output"
        >
          {built.caption ? built.caption : <span className="text-muted-foreground">(an empty caption)</span>}
          {built.caption && built.caption.trim() === '' && (
            <span className="text-muted-foreground" data-testid="repost-caption-preview-invisible">
              (only spaces that do not show, such as a no-break space - the worker posts them as they are)
            </span>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground" data-testid="repost-caption-preview-facts">{facts.join(' - ')}</p>

      {notes.length > 0 && (
        <ul className="space-y-1 rounded-md border border-warning/40 bg-warning/10 p-2 text-xs" data-testid="repost-caption-preview-notes">
          {notes.map((n) => (
            <li key={n.text} className={`flex items-start gap-1.5 ${NOTE_CLASS[n.kind] || ''}`} data-kind={n.kind}>
              {n.kind !== 'info' && <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
              <span>{n.text}</span>
            </li>
          ))}
        </ul>
      )}

      {versions && (
        <details className="text-xs" data-testid="repost-caption-preview-versions">
          <summary className="cursor-pointer text-muted-foreground">All {versions.length} versions of your text (before the tokens are filled)</summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            {versions.map((v) => (
              <li key={v} className="whitespace-pre-wrap break-words font-mono text-foreground">{v}</li>
            ))}
          </ol>
        </details>
      )}

      <p className="text-xs text-muted-foreground">
        On the phone, 653/654/655 clean the real original caption first, and the hashtags of 646-648 are added at the end.
      </p>
    </div>
  );
}
